import {
  SVG_CLOSE_FILLED,
  SVG_CHEVRON_LEFT_OUTLINED,
  SVG_CHEVRON_RIGHT_OUTLINED,
  SVG_SEARCH_OUTLINED,
} from '@getgo/chameleon-icons'
import { html, state } from 'lit-element'
import { repeat } from 'lit-html/directives/repeat'
import { unsafeSVG } from 'lit-html/directives/unsafe-svg'
import { ShellElement } from '../../../common/shell-element'
import { t } from '../../../directives/translate'
import type { GlobalSearchResultItem } from '../../../extensions'
import { executeCommand } from '../../../services/namespaces/utils'
import { getEventBus } from '../../../services/namespaces/event-bus'
import { getGlobalSearchManager } from '../global-search-manager'
import { GlobalSearchQueryUI } from '../global-search-models'
import type { GlobalSearchActionUI, GlobalSearchCategoryUI, GlobalSearchColumnUI } from '../global-search-models'
import { closeSearchPage, getCurrentSearchQuery } from '../helpers'
import type { globalSearchEvents, GlobalSearchQueryEventPayload } from '../namespace'
import { GLOBAL_SEARCH_NAMESPACE } from '../namespace'
import searchResultsPageStyles from './search-results-page.styles.scss'

export interface CategorySection {
  readonly category: GlobalSearchCategoryUI
  // eslint-disable-next-line functional/prefer-readonly-type
  readonly results: GlobalSearchResultItem[]
}
const ROWS_PER_PAGE_OPTIONS = [25, 50, 100]

export class SearchResultsPage extends ShellElement {
  static readonly tagName = 'goto-search-results-page'
  @state() private searchQuery: GlobalSearchQueryUI = { query: '' }
  @state() private searchResults: readonly GlobalSearchResultItem[] = []
  @state() private resultsPerPage: GlobalSearchResultItem[] = []
  @state() private selectedRowsPerPage = ROWS_PER_PAGE_OPTIONS[0]
  @state() private pageNumerator = 1
  @state() private pageDenominator = 1

  private categoryResults: Map<string, GlobalSearchResultItem[]> = new Map<string, GlobalSearchResultItem[]>()
  private categories: readonly GlobalSearchCategoryUI[] = []

  static get styles() {
    return searchResultsPageStyles
  }

  connectedCallback() {
    super.connectedCallback()
    this.categories = getGlobalSearchManager().getCategories()
    this.searchQuery = getCurrentSearchQuery()

    const { resultPageQueryChange } = getEventBus().subscribeTo<
      typeof GLOBAL_SEARCH_NAMESPACE,
      typeof globalSearchEvents
    >(GLOBAL_SEARCH_NAMESPACE)
    resultPageQueryChange.addListener(this.queryListener)
    this.unsubscribeFunctions.push(() => {
      resultPageQueryChange.removeListener(this.queryListener)
    })
  }

  private queryListener = (event: GlobalSearchQueryEventPayload) => {
    this.searchQuery = event.query
  }

  updated(changedProperties: Map<string, unknown>) {
    changedProperties.forEach((oldValue, propName) => {
      if (propName === 'searchQuery') {
        this.executeSearch()
      }
      if (propName === 'searchResults') {
        this.updateCategoryResults()
      }
    })
  }

  private async executeSearch() {
    this.searchResults = await getGlobalSearchManager().searchFor(this.searchQuery)
  }

  private updateCategoryResults() {
    this.categoryResults.clear()
    this.searchResults.forEach(result => {
      const category = getGlobalSearchManager().getCategory(result)
      if (category) {
        const categoryResults = this.categoryResults.get(category.application)
        if (categoryResults) {
          categoryResults.push(result)
        } else {
          this.categoryResults.set(category.application, [result])
        }
      }
    })
    this.requestUpdate()
  }

  private executeDefaultActionCommand(result: GlobalSearchResultItem) {
    const defaultAction = getGlobalSearchManager().getResultDefaultAction(result, 'list')
    const params = [result, this.searchQuery.query]
    if (defaultAction?.command) {
      executeCommand(defaultAction.command, [...params])
    }
  }

  private renderTableHeader(columns: readonly GlobalSearchColumnUI[]) {
    return html`<chameleon-data-table-header>
      <chameleon-data-table-row>
        ${repeat(
          columns,
          column => column.id,
          column =>
            html`<chameleon-data-table-cell class="column-header">${column.displayName}</chameleon-data-table-cell>`,
        )}
      </chameleon-data-table-row>
    </chameleon-data-table-header>`
  }

  private renderTableBody(results: GlobalSearchResultItem[], columns: readonly GlobalSearchColumnUI[]) {
    this.resultsPerPage = [...results]
    if (this.selectedRowsPerPage < results.length) {
      this.resultsPerPage.length = this.selectedRowsPerPage
    }
    return html`<chameleon-data-table-body>
      ${repeat(
        this.resultsPerPage,
        result =>
          html`<chameleon-data-table-row
            class="table-body-data-row"
            @click=${() => this.executeDefaultActionCommand(result)}
            >${this.renderDataTableCells(result, columns)}</chameleon-data-table-row
          >`,
      )}
    </chameleon-data-table-body>`
  }

  private renderDataTableCells(result: GlobalSearchResultItem, columns: readonly GlobalSearchColumnUI[]) {
    return html`${repeat(
      columns,
      column => column.id,
      column =>
        html`<chameleon-data-table-cell class="table-body-data-cells"
          >${getGlobalSearchManager().getResultFieldValue(result, column.valueField)}</chameleon-data-table-cell
        >`,
    )}`
  }

  private renderRowsPerPageOptions() {
    return html`${repeat(
      ROWS_PER_PAGE_OPTIONS,
      result => html` <chameleon-option value=${result}>${result}</chameleon-option>`,
    )} `
  }

  private renderRowsPerPage() {
    return html`<div class="rows-per-page">
      <chameleon-typography variant="caption-medium-strong" color="text-02">${t('Rows per page')}</chameleon-typography>
      <chameleon-select data-test="rows-per-page-select" @change=${this.handleSelectRows} size="small">
        ${this.renderRowsPerPageOptions()}
      </chameleon-select>
    </div>`
  }

  private renderPageSelector() {
    return html`<div class="page-selector">
      <chameleon-tooltip-v2>
        <chameleon-icon-button
          class="previous-page-button-data"
          disabled=${this.pageNumerator === 1}
          @click="${() => {
            this.previousPage()
          }}"
          size="small"
          variant="neutral"
          slot="trigger"
          id="cham-popover-trigger-86"
        >
          <chameleon-svg>${SVG_CHEVRON_LEFT_OUTLINED}</chameleon-svg>
        </chameleon-icon-button>
        <span slot="content">${t('Previous page')}</span>
      </chameleon-tooltip-v2>
      <chameleon-typography variant="caption-medium-strong" color="text-02">
        ${t(`Page {{pageNumerator}} of {{pageDenominator}}`, () => ({
          pageNumerator: this.pageNumerator,
          pageDenominator: this.pageDenominator,
        }))}
      </chameleon-typography>
      <chameleon-tooltip-v2>
        <chameleon-icon-button
          class="next-page-button-data"
          disabled=${this.pageNumerator === this.pageDenominator}
          @click="${() => {
            this.nextPage()
          }}"
          size="small"
          variant="neutral"
          slot="trigger"
          id="cham-popover-trigger-86"
        >
          <chameleon-svg>${SVG_CHEVRON_RIGHT_OUTLINED}</chameleon-svg>
        </chameleon-icon-button>
        <span slot="content">${t('Next page')}</span>
      </chameleon-tooltip-v2>
    </div>`
  }

  private renderPagerCount(results: GlobalSearchResultItem[]) {
    const firstResult = this.pageNumerator === 1 ? 1 : 1 + this.selectedRowsPerPage * (this.pageNumerator - 1)
    const lastResult = Math.min(this.selectedRowsPerPage * this.pageNumerator, results.length)

    return html`<div class="pager-count">
      <chameleon-typography variant="caption-medium-strong" color="text-02">
        ${t(`{{firstResult}} - {{lastResult}} of {{length}}`, () => ({
          firstResult: firstResult,
          lastResult: lastResult,
          length: this.searchResults.length,
        }))}
      </chameleon-typography>
      ${this.renderPageSelector()}
    </div>`
  }

  private handleClick = () => {
    closeSearchPage()
  }

  private async handleSelectRows(event: CustomEvent) {
    this.selectedRowsPerPage = parseInt((event.target as HTMLInputElement).value)
    this.pageNumerator = 1
  }

  private async previousPage() {
    this.pageNumerator--
    // TODO in SCORE-2329
  }

  private async nextPage() {
    this.pageNumerator++
    // TODO in SCORE-2329
  }

  private renderSearchQueryHeading() {
    return html`<chameleon-typography variant="heading-small" class="heading-text">
        ${t('Search results for')} "${this.searchQuery.query}"
      </chameleon-typography>
      <chameleon-icon-button variant="neutral" size="small" class="close-button" @click=${this.handleClick}>
        <chameleon-svg>${unsafeSVG(SVG_CLOSE_FILLED)}</chameleon-svg>
      </chameleon-icon-button>`
  }

  private renderCategoryTableResults(results: GlobalSearchResultItem[]) {
    return results.length
      ? html`<chameleon-data-table>
          ${this.renderTableHeader(this.categories[0].columns)}
          ${this.renderTableBody(results, this.categories[0].columns)}
        </chameleon-data-table>`
      : html`<chameleon-data-table> ${this.renderTableHeader(this.categories[0].columns)} </chameleon-data-table>
          ${this.renderNoResults()}`
  }

  private renderNoResultsAction(action: GlobalSearchActionUI) {
    return html`<div class="no-results-action">
      <chameleon-button
        size="small"
        variant="tertiary"
        @click=${(event: MouseEvent) => {
          event.stopPropagation()
          executeCommand(action.command, this.searchQuery.query)
        }}
      >
        <chameleon-svg slot="start">${unsafeSVG(action.icon)}</chameleon-svg>
        ${action.displayName}
      </chameleon-button>
    </div>`
  }

  private renderNoResults() {
    return html`<chameleon-empty-state size="small">
      <chameleon-svg slot="icon">${unsafeSVG(SVG_SEARCH_OUTLINED)}</chameleon-svg>
      <div slot="actions">
        ${repeat(
          this.categories[0].emptyState.actions,
          action => action.id,
          action => this.renderNoResultsAction(action),
        )}
      </div>
      ${this.categories[0].emptyState.message}
    </chameleon-empty-state>`
  }

  render() {
    // We are only getting one category for phase one. Logic will need to be adapted once we have the possibility of multiple categories
    const results = this.categoryResults.get(this.categories[0].application) ?? []
    if (results.length) {
      this.pageDenominator = Math.ceil(results.length / this.selectedRowsPerPage)
    }
    return html`<div class="search-query-heading">${this.renderSearchQueryHeading()}</div>
      <div class="results-table">${this.renderCategoryTableResults(results)}</div>
      <div class="results-table-pager pager">${this.renderRowsPerPage()} ${this.renderPagerCount(results)}</div>`
  }
}

declare global {
  interface HTMLElementTagNameMap {
    readonly [SearchResultsPage.tagName]: SearchResultsPage
  }
}
